import { useMemo } from 'react'

import { useBranches } from '@/pages/User/pages/Branches/hooks/useBranches'
import {
	useGetApprovalsQuery,
	useGetEventsForMergeRequestQuery,
	useGetMergeRequestQuery,
	useGetMergeRequestsInSystemQuery,
	useGetResolutionsQuery,
} from '@/rtkApi'
import type { MergeRequestStatusEnum } from '@/rtkApi/mddApiSliceGeneratedV2'

import { mapMergeRequestDto } from './mapMergeRequest'
import { MergeRequestRecord, MergeRequestStatus } from './types'

const refetchOpts = { refetchOnMountOrArgChange: true } as const
export const MERGE_REQUESTS_PAGE_SIZE = 20

const apiStatuses = (status?: MergeRequestStatus): MergeRequestStatusEnum[] | undefined =>
	status === 'OPEN' ? ['OPEN', 'DRAFT'] : status ? [status] : undefined

export const useMergeRequests = (
	mrId?: string,
	options: { page?: number; status?: MergeRequestStatus } = {},
) => {
	const {
		branches,
		hasSystemId,
		isError: branchesError,
		isLoading: branchesLoading,
		systemId,
	} =
		useBranches()
	const listQuery = useGetMergeRequestsInSystemQuery(
		{
			systemId: systemId as number,
			status: apiStatuses(options.status),
			page: options.page ?? 1,
			pageSize: MERGE_REQUESTS_PAGE_SIZE,
		},
		{ skip: !hasSystemId, ...refetchOpts },
	)
	const selectedId = mrId != null ? Number(mrId) : Number.NaN
	const hasSelectedId = mrId != null && !Number.isNaN(selectedId)
	const selectedQuery = useGetMergeRequestQuery(
		{ mergeRequestId: selectedId },
		{ skip: !hasSelectedId, ...refetchOpts },
	)
	const approvalsQuery = useGetApprovalsQuery(
		{ mergeRequestId: selectedId },
		{ skip: !hasSelectedId, ...refetchOpts },
	)
	const resolutionsQuery = useGetResolutionsQuery(
		{ mergeRequestId: selectedId },
		{ skip: !hasSelectedId, ...refetchOpts },
	)
	const eventsQuery = useGetEventsForMergeRequestQuery(
		{ mergeRequestId: selectedId },
		{ skip: !hasSelectedId, ...refetchOpts },
	)

	const mergeRequests = useMemo(
		() => (listQuery.data?.content ?? []).map((dto) => mapMergeRequestDto(dto, branches)),
		[branches, listQuery.data?.content],
	)

	const selected: MergeRequestRecord | null = useMemo(() => {
		if (!hasSelectedId) return null
		if (selectedQuery.data) {
			return mapMergeRequestDto(
				selectedQuery.data,
				branches,
				approvalsQuery.data,
				resolutionsQuery.data,
				eventsQuery.data,
			)
		}
		return mergeRequests.find((item) => item.id === mrId) ?? null
	}, [
		approvalsQuery.data,
		branches,
		eventsQuery.data,
		hasSelectedId,
		mergeRequests,
		mrId,
		resolutionsQuery.data,
		selectedQuery.data,
	])

	const refetch = () => {
		if (!listQuery.isUninitialized) void listQuery.refetch()
		if (hasSelectedId) {
			if (!selectedQuery.isUninitialized) void selectedQuery.refetch()
			if (!approvalsQuery.isUninitialized) void approvalsQuery.refetch()
			if (!resolutionsQuery.isUninitialized) void resolutionsQuery.refetch()
			if (!eventsQuery.isUninitialized) void eventsQuery.refetch()
		}
	}

	const isFetching =
		listQuery.isFetching ||
		selectedQuery.isFetching ||
		approvalsQuery.isFetching ||
		resolutionsQuery.isFetching ||
		eventsQuery.isFetching

	return {
		branches,
		hasSystemId,
		isError: branchesError || listQuery.isError,
		isFetching,
		isLoading:
			hasSystemId &&
			(branchesLoading || listQuery.isLoading),
		mergeRequests,
		page: listQuery.data?.page ?? options.page ?? 1,
		refetch,
		selected,
		totalElements: listQuery.data?.totalElements ?? 0,
		totalPages: Math.max(1, listQuery.data?.totalPages ?? 1),
	}
}
